import {
  colors,
  ImpAccountStatus,
  ImpButton,
  ImpContainer,
  ImpSpacer,
  ImpText,
  MaxWidth,
} from '@imprint-payments/imprint-ui';
import { useEffect } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { setRedirectToLogin } from '../../app/acceptScreenSlice';
import { requireFullSSN } from '../../app/applicationScreenSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import { setMultiCardUser } from '../../app/loginSlice';
import { SCREEN, setScreen } from '../../app/screenStateSlice';
import {
  findAccountDisplayURL,
  findAccountURL,
  supportLink,
  transUnionArticleURL,
  transUnionLoginURL,
  webServicingDisplayURL,
  webServicingURL,
} from '../../assets/consts/const';
import { HeaderHandler } from '../../components/HeaderHandler/HeaderHandler';
import WaterfallModal from '../../components/modal/WaterfallModal';
import { ANALYTICS } from '../../firebase/firebase';
import { redirectToAccountWithCookie } from '../../utils/functionUtils';
import { PAGE } from '../../utils/routeConstant';
import {
  emitSDKEvent,
  emitSDKResult,
  isSDK,
  SDK_EVENTS,
  useSDKContext,
} from '../../utils/sdk';

const MAX_CONTENT_WIDTH = `min(${MaxWidth.Button}, 100vw - 32px)`;

type AccountStatusPageProps = {
  isFullScreen?: boolean;
};

export default function AccountStatusPage({
  isFullScreen,
}: AccountStatusPageProps) {
  const {
    language,
    merchantData,
    screenState,
    consumerInfo,
    appStatus,
    apiScreenState,
    dispatch,
    navigate,
  } = useImpState();

  const { result } = useSDKContext();

  useEffect(() => {
    if (screenState.screen === SCREEN.APPLICATION_IN_REVIEW) {
      return;
    }
    if (
      screenState.screen === SCREEN.APPLICATION_NOT_APPROVED &&
      !appStatus.showCounterOffer
    ) {
      return;
    }
    if (screenState.screen === SCREEN.CREDIT_FROZEN) {
      return;
    }
  }, [screenState.screen, appStatus.showCounterOffer]);

  const loading = useAppSelector(
    (state) => state.applicationScreenSelector.loading
  );

  const paragraphStyle: React.CSSProperties = {
    whiteSpace: 'pre-line',
  };

  const renderLogIntoAccountBody = () => {
    return (
      <ImpContainer textAlign="center">
        <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
          {language.duplicateAppBody}
          <ImpButton
            variant="inline"
            buttonStyle="color"
            color={colors.content.onSurfaceVariant}
            onClick={() => {
              window.open(webServicingURL, '_blank');
            }}
          >
            {webServicingDisplayURL}
          </ImpButton>
          {language.duplicateAppBody2}
        </ImpText>
      </ImpContainer>
    );
  };

  const handleLogIntoAccount = () => {
    // TODO: this is not necessarily true. Should use different naming
    const isMultiCard = 'true';
    ANALYTICS.logEvent(EVENT.RETURNING_ACTIVE_USER_REDIRECT_TO_ACCOUNT, {
      product_uuid: merchantData.productUUID,
    });
    dispatch(setRedirectToLogin(true));
    redirectToAccountWithCookie(
      consumerInfo.auth.token,
      apiScreenState.deviceID,
      consumerInfo.consumerUUID,
      merchantData.productUUID,
      isMultiCard
    );
  };

  const renderOfferWaitingBody = () => {
    return (
      <ImpContainer textAlign="center">
        <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
          {language.appStatusHaveOfferBody}
          <ImpButton
            variant="inline"
            buttonStyle="color"
            color={colors.content.onSurfaceVariant}
            onClick={() => {
              window.open(findAccountURL, '_blank');
            }}
          >
            {findAccountDisplayURL}
          </ImpButton>
          {language.period}
        </ImpText>
      </ImpContainer>
    );
  };

  const renderFindAccountRequiredBody = () => {
    return (
      <ImpContainer textAlign="center">
        <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
          {language.appStatusFindAccountRequiredBody1}
          <ImpButton
            variant="inline"
            buttonStyle="color"
            color={colors.content.onSurfaceVariant}
            onClick={() => {
              window.open(findAccountURL, '_blank');
            }}
          >
            {findAccountDisplayURL}
          </ImpButton>
          {language.appStatusFindAccountRequiredBody2}
        </ImpText>
      </ImpContainer>
    );
  };

  const getButtonState = (loading: boolean) => {
    if (loading) {
      return 'loading';
    } else {
      return 'enabled';
    }
  };

  const renderLogIntoAccountButton = () => {
    return (
      <ImpButton
        variant="primary"
        buttonStyle="branded"
        size="fill"
        state={getButtonState(loading)}
        onClick={handleLogIntoAccount}
      >
        {language.logIn}
      </ImpButton>
    );
  };

  const renderNotApprovedBody = () => {
    return (
      <ImpContainer>
        <ImpSpacer height="xs" />
        <ImpText typography="body2">
          {language.applicationNotApprovedBody.replace(
            '$cardName$',
            merchantData.rewardDetails.cardName
          )}
        </ImpText>
      </ImpContainer>
    );
  };

  const frozenCreditHeader = () => {
    return language.afterOtpAppStatusFrozenCreditHeader;
  };

  const renderFrozenCreditBody2 = () => {
    return (
      <ImpContainer>
        <ImpSpacer height="m" />
        <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
          <span style={paragraphStyle}>
            {language.enumerateOne}
            <ImpButton
              variant="inline"
              onClick={() => {
                window.open(transUnionLoginURL, '_blank');
              }}
            >
              {language.logIn}
            </ImpButton>
            {language.creditFrozenBody2}
          </span>
        </ImpText>
      </ImpContainer>
    );
  };

  const renderFrozenCreditBody3 = () => {
    return (
      <ImpContainer>
        <ImpSpacer height="m" />
        <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
          <span style={paragraphStyle}>
            {language.creditFrozenBody3ReApplication.replace(
              '$expireDate$',
              appStatus.expiresAt
            )}
            <ImpButton
              variant="inline"
              onClick={() => {
                window.open(transUnionArticleURL, '_blank');
              }}
            >
              {language.thisArticle}
            </ImpButton>
            {language.creditFrozenBody5}
          </span>
        </ImpText>
      </ImpContainer>
    );
  };

  const handleUnfreezeCredit = () => {
    dispatch(setScreen(SCREEN.APPLICATION));
    navigate(PAGE.HOME);
    if (appStatus.reApplicationInApp) {
      dispatch(requireFullSSN(false));
      dispatch(setMultiCardUser(false));
    }
    ANALYTICS.logEvent(EVENT.APPLICATION_DECISION_FROZEN_ACTION_CLICKED, {
      product_uuid: merchantData.productUUID,
    });
  };

  const renderUnfreezeCreditButton = () => {
    return (
      <ImpContainer textAlign="center">
        <ImpButton
          variant="primary"
          buttonStyle="branded"
          size="m"
          state={getButtonState(loading)}
          onClick={() => handleUnfreezeCredit()}
        >
          {language.unfrozeMyCredit}
        </ImpButton>
        {isSDK() && (
          <>
            <ImpSpacer height="s" />
            {renderBackToPartnerButton(false)}
          </>
        )}
      </ImpContainer>
    );
  };

  const handleBackToPartner = () => {
    emitSDKResult({
      result,
    });

    emitSDKEvent({
      event: SDK_EVENTS.CLOSED,
    });
  };

  const renderBackToPartnerButton = (isPrimary: boolean) => {
    return (
      <ImpButton
        variant={isPrimary ? 'primary' : 'tertiary'}
        buttonStyle="branded"
        size="fill"
        state={getButtonState(loading)}
        onClick={handleBackToPartner}
      >
        {language.backTo + merchantData.rewardDetails.merchantName}
      </ImpButton>
    );
  };

  const renderManualReviewBody = () => {
    return (
      <ImpContainer textAlign="left">
        <ImpSpacer height="xs" />
        <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
          {language.applicationInReviewBody2}
          <ImpButton
            variant="inline"
            onClick={() => {
              window.location.href = `https://${supportLink}`;
            }}
          >
            {supportLink}
          </ImpButton>
          {language.period}
        </ImpText>
      </ImpContainer>
    );
  };

  const handleViewDeclineDetailsClick = () => {
    ANALYTICS.logEvent(EVENT.APP_DECLINE_LINK_CLICKED, {
      link: 'view_decline_details',
    });
    if (appStatus.rejectionReasonsURL !== '')
      window.open(appStatus.rejectionReasonsURL);
  };

  const renderAccountStatus = () => {
    switch (screenState.screen) {
      case SCREEN.APPLICATION_IN_REVIEW: {
        return (
          <ImpAccountStatus
            header={language.afterOtpAppStatusinReviewHeader}
            body1={language.applicationInReviewBody}
            body2={renderManualReviewBody()}
            buttons={isSDK() && renderBackToPartnerButton(true)}
            showMobileDivider
            width={MAX_CONTENT_WIDTH}
          />
        );
      }
      case SCREEN.APPLICATION_NOT_APPROVED: {
        if (appStatus.showCounterOffer) {
          return (
            <WaterfallModal
              handleViewDeclineDetailsClick={handleViewDeclineDetailsClick}
            />
          );
        } else {
          return (
            <ImpAccountStatus
              header={language.applicationNotApprovedTitle}
              body1={renderNotApprovedBody()}
              buttons={isSDK() && renderBackToPartnerButton(true)}
              showMobileDivider
              width={MAX_CONTENT_WIDTH}
            />
          );
        }
      }
      case SCREEN.CREDIT_FROZEN: {
        return (
          <ImpAccountStatus
            header={frozenCreditHeader()}
            body1={language.creditFrozenBody}
            body2={renderFrozenCreditBody2()}
            body3={renderFrozenCreditBody3()}
            buttons={renderUnfreezeCreditButton()}
            showMobileDivider
            width={MAX_CONTENT_WIDTH}
          />
        );
      }
      case SCREEN.APPLICATION_EXPIRED: {
        return <ImpAccountStatus header={language.offerHasExpiredTitle} />;
      }
      case SCREEN.APPLICATION_ACTIVE_USER: {
        return (
          <ImpAccountStatus
            header={language.duplicateAppTitle}
            body1={renderLogIntoAccountBody()}
            buttons={
              isSDK()
                ? renderBackToPartnerButton(true)
                : renderLogIntoAccountButton()
            }
            headerCenterAlign
          />
        );
      }
      case SCREEN.APPLICATION_OFFER_NOT_ACCEPTED: {
        return (
          <ImpAccountStatus
            header={language.appStatusHaveOfferHeader}
            body1={renderOfferWaitingBody()}
            buttons={isSDK() && renderBackToPartnerButton(true)}
            headerCenterAlign
          />
        );
      }

      case SCREEN.FIND_ACCOUNT_REQUIRED: {
        return (
          <ImpAccountStatus
            header={language.appStatusFindAccountRequiredHeader}
            body1={renderFindAccountRequiredBody()}
            buttons={isSDK() && renderBackToPartnerButton(true)}
            headerCenterAlign
          />
        );
      }
      case SCREEN.ACCOUNT_LOCKED: {
        return (
          <ImpAccountStatus
            header={language.loginOtpFailedHeader}
            body1={language.accountLockedBody}
            headerCenterAlign
            body1CenterAlign
            width={MAX_CONTENT_WIDTH}
          />
        );
      }
      case SCREEN.STATUS_OTP_VERIFICATION_FAILED: {
        return (
          <ImpAccountStatus
            header={language.phoneCantBeVerified}
            body1={language.appStatusFailedBody}
            headerCenterAlign
            body1CenterAlign
            width={MAX_CONTENT_WIDTH}
          />
        );
      }
      case SCREEN.VERIFICATION_FAILED: {
        return (
          <ImpAccountStatus
            header={''}
            body1={language.maxRetriesBody}
            headerCenterAlign
            body1CenterAlign
            width={MAX_CONTENT_WIDTH}
          />
        );
      }
    }
  };

  return (
    <>
      {isFullScreen && <HeaderHandler />}
      {renderAccountStatus()}
    </>
  );
}
